import React, { useEffect, useState } from "react";
import "../../styles/styles.css";
import "../../styles/layout.css";
import { Row, Col, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import LeaveViewController from "../../redux/actions/leaveApiAction";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { Box, Typography, Grid } from "@mui/material";

// Register the chart components
ChartJS.register(ArcElement, Tooltip, Legend);

const LeaveBalanceChart = () => {
  const dispatch = useDispatch();
  const { getLeaveBalance  } = LeaveViewController();
  const { leaveBalance } = useSelector((state) => state.leave);

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    dispatch(getLeaveBalance ());
  }, []);

  

  const generateLeaveYears = () => {
    const currentYear = new Date().getFullYear();
    const startYear = 2020; // or any logical lower limit
    const years = [];
    for (let year = currentYear; year >= startYear; year--) {
      years.push(year);
    }
    return years;
  };

  // Create chart data based on the leave balance
  const selectedBalance = leaveBalance?.find(
    (balance) => Number(balance.leave_year) === Number(selectedYear)
  );
  

  const generatePieChartData = () => {
    const paid = selectedBalance?.remaining_paid_leave_balance || 0;
    const unpaid = selectedBalance?.remaining_unpaid_leave_balance || 0;
    const carry = selectedBalance?.carry_forward_days || 0;

    return {
      labels: ["Paid Leave", "Unpaid Leave", "Carry Forward"],
      datasets: [
        {
          data: [paid, unpaid, carry],
          backgroundColor: ["#4caf50", "#f44336", "#ff9800"],
        },
      ],
    };
  };

  if (isMobile) {
    return (
      <Box sx={{ pb: 'calc(80px + env(safe-area-inset-bottom, 0px))', bgcolor: 'var(--brand-bg, #f8fafc)', minHeight: '100%', p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--brand-navy, #1a2b4a)' }}>
            My Leave Balance
          </Typography>
          <Form.Select
            size="sm"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{ width: '110px', borderRadius: '8px', border: '1px solid var(--brand-border, #e5e7eb)' }}
          >
            {generateLeaveYears().map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Form.Select>
        </Box>

        <Grid container spacing={2} sx={{ mb: 4 }}>
          {/* PAID LEAVES */}
          <Grid item xs={6}>
            <Box sx={{ p: 2, background: 'linear-gradient(135deg, #a18cd1, #fbc2eb)', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxSizing: 'border-box' }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(0,0,0,0.7)', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                Paid Leaves
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#000', lineHeight: 1 }}>
                  {selectedBalance?.remaining_paid_leave_balance || 0}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'rgba(0,0,0,0.6)' }}>
                  / {selectedBalance?.paid_leave_balance || 0}
                </Typography>
              </Box>
            </Box>
          </Grid>
          {/* UNPAID LEAVES */}
          <Grid item xs={6}>
            <Box sx={{ p: 2, background: 'linear-gradient(135deg, #007bff, #cce5ff)', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxSizing: 'border-box' }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(0,0,0,0.7)', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                Unpaid Leaves
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#000', lineHeight: 1 }}>
                  {selectedBalance?.remaining_unpaid_leave_balance || 0}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'rgba(0,0,0,0.6)' }}>
                  / {selectedBalance?.unpaid_leave_balance || 0}
                </Typography>
              </Box>
            </Box>
          </Grid>
          {/* CARRY FORWARD LEAVES */}
          <Grid item xs={12}>
            <Box sx={{ p: 2, background: 'linear-gradient(135deg, #357e46, #d4edda)', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxSizing: 'border-box' }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(0,0,0,0.7)', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                Carry Forward Leaves
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#000', lineHeight: 1 }}>
                  {selectedBalance?.carry_forward_days || 0}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'rgba(0,0,0,0.6)' }}>
                  / {selectedBalance?.carry_forward_days || 0}
                </Typography>
              </Box>
            </Box>
          </Grid>
          {/* TOTAL LEAVES */}
          <Grid item xs={6}>
            <Box sx={{ p: 2, background: 'linear-gradient(135deg, #c32230, #f8d7da)', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxSizing: 'border-box' }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(0,0,0,0.7)', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                Total Leaves
              </Typography>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#000', lineHeight: 1 }}>
                {selectedBalance?.total_leave_balance || 0}
              </Typography>
            </Box>
          </Grid>
          {/* REMAINING LEAVES */}
          <Grid item xs={6}>
            <Box sx={{ p: 2, background: 'linear-gradient(135deg, #55585e, #e2e3e5)', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxSizing: 'border-box' }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(0,0,0,0.7)', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1 }}>
                Remaining Leaves
              </Typography>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#000', lineHeight: 1 }}>
                {selectedBalance?.remaining_total_leave_balance || 0}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ p: 3, bgcolor: '#fff', borderRadius: '24px', border: '1px solid var(--brand-border, #e5e7eb)', boxShadow: '0 4px 12px rgba(15,23,42,0.03)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'var(--brand-navy, #1a2b4a)', mb: 3, width: '100%', textAlign: 'left' }}>
            Leave Distribution
          </Typography>
          <Box sx={{ width: '220px', height: '220px', position: 'relative' }}>
            <Pie
              data={generatePieChartData()}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  centerText: {
                    display: true,
                    text: `${selectedBalance?.remaining_total_leave_balance || 0} Left`
                  }
                },
                cutout: '75%',
              }}
              plugins={[{
                id: 'centerText',
                beforeDraw: (chart) => {
                  const { width, height, ctx } = chart;
                  ctx.restore();
                  const fontSize = 20;
                  ctx.font = `bold ${fontSize}px sans-serif`;
                  ctx.textBaseline = "middle";
                  ctx.fillStyle = "#1a2b4a";
                  const text = chart.options.plugins.centerText.text;
                  const textX = Math.round((width - ctx.measureText(text).width) / 2);
                  const textY = height / 2;
                  ctx.fillText(text, textX, textY);
                  ctx.save();
                }
              }]}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center', mt: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4caf50' }} />
              <Typography sx={{ fontSize: '0.75rem', color: 'var(--brand-muted, #6b7280)', fontWeight: 500 }}>Paid</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#f44336' }} />
              <Typography sx={{ fontSize: '0.75rem', color: 'var(--brand-muted, #6b7280)', fontWeight: 500 }}>Unpaid</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#ff9800' }} />
              <Typography sx={{ fontSize: '0.75rem', color: 'var(--brand-muted, #6b7280)', fontWeight: 500 }}>Carry</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <div>
       
      {/* Top right dropdown */}
      <div className="d-flex justify-content-end align-items-center mb-2">
        <Form.Group controlId="yearSelect" className="d-flex align-items-center">
          <Form.Label className="me-2 fw-bold mb-0">Select Year:</Form.Label>
          <Form.Select
            size="sm"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{ width: '150px'}}
          >
            {generateLeaveYears().map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Form.Select>
        </Form.Group>
      </div>
        
          <Row className="mt-2">
                 <Col>
                   <div className="card d-flex d-flex-direction-row align-items-center"
                   style={{ background: 'linear-gradient(135deg, #a18cd1 , #fbc2eb )', padding: '16px', borderRadius: '8px',height: '100px',width: '100%' }}
                    >
                     <div className="d-flex d-flex-direction-column">
                       <h6 style={{color: 'black', fontSize:"0.8125rem", fontFamily:'sans-serif'}} >
                         PAID LEAVES
                       </h6>
                       <h3 className="value m-0">
                        <span style={{ color: '#d9d9d9' }}>
                        {selectedBalance?.remaining_paid_leave_balance || 0}
                       </span>/
                      {selectedBalance?.paid_leave_balance || 0}</h3>
                     </div>
                   </div>
                 </Col>
                 <Col>
                   <div className="card d-flex d-flex-direction-row align-items-center"
                   style={{ background: 'linear-gradient(135deg, #007bff , #cce5ff )', padding: '16px', borderRadius: '8px', height: '100px',width: '100%'}}
                   >
                     <div className="d-flex d-flex-direction-column">
                       <h6 style={{color: 'black', fontSize:"0.8125rem", fontFamily:'sans-serif'}}> UNPAID LEAVES</h6>
                       <h3 className="value mb-0">
                        <span style={{ color: '#d9d9d9' }}>
                          {selectedBalance?.remaining_unpaid_leave_balance || 0}
                          </span>/
                          {selectedBalance?.unpaid_leave_balance || 0}</h3>
                     </div>
                   </div>
                 </Col>
                 <Col>
                   <div className="card d-flex d-flex-direction-row align-items-center" 
                   style={{ background: 'linear-gradient(135deg, #357e46 , #d4edda )', padding: '16px', borderRadius: '8px',height: '100px',width: '100%' }}>
                     <div className="d-flex d-flex-direction-column">
                       <h6 style={{color: 'black', fontSize:"0.8125rem", fontFamily:'sans-serif'}} >CARRY FORWORD LEAVES</h6>
                       <h3 className="value mb-0">
                        <span style={{ color: '#d9d9d9' }}>
                        {selectedBalance?.carry_forward_days || 0}
                        </span>/
                        {selectedBalance?.carry_forward_days || 0}</h3>
                     </div>
                   </div>
                 </Col>
                 <Col>
                   <div className="card d-flex d-flex-direction-row align-items-center" 
                   style={{ background: 'linear-gradient(135deg, #c32230 , #f8d7da )', padding: '16px', borderRadius: '8px',height: '100px',width: '100%' }}>
                     <div className="d-flex d-flex-direction-column">
                       <h6 style={{color: 'black', fontSize:"0.8125rem", fontFamily:'sans-serif'}} > TOTAL LEAVES</h6>
                       <h3 className="value mb-0">{selectedBalance?.total_leave_balance || 0}</h3>
                     </div>
                   </div>
                 </Col>
                 <Col>
                   <div className="card d-flex d-flex-direction-row align-items-center"
                   style={{background: 'linear-gradient(135deg, #55585e , #e2e3e5 )', padding: '16px', borderRadius: '8px',height: '100px',width: '100%' }}>
                     <div className="d-flex d-flex-direction-column">
                       <h6 style={{color: 'black', fontSize:"0.8125rem", fontFamily:'sans-serif'}} > REMANING LEAVES </h6>
                       <h3 className="value mb-0">{selectedBalance?.remaining_total_leave_balance || 0}</h3>
                     </div>
                   </div>
                 </Col>
                
               </Row>
        
          {/* Render the Pie chart for Employee */}
          <div  style={{ height: '80vh', paddingLeft: '0%' }}>
            <div className="d-flex flex-wrap" style={{ gap: '50px', alignItems: 'center' }}>
              {/* Pie Chart */}
              <div style={{ width: '300px', height: '300px' }}>
              <Pie
                  data={generatePieChartData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                      // Custom Plugin to render text in center
                      centerText: {
                        display: true,
                        text: ` Remaning Leaves : ${ (selectedBalance?.remaining_total_leave_balance || 0)}`
                      }
                    },
                    cutout: '80%',
                  }}
                  plugins={[{
                    id: 'centerText',
                    beforeDraw: (chart) => {
                      const { width } = chart;
                      const { height } = chart;
                      const ctx = chart.ctx;
                      ctx.restore();
                      const fontSize = 18;
                      ctx.font = `${fontSize}px Arial`;
                      ctx.textBaseline = "middle";

                      const text = chart.options.plugins.centerText.text;
                      const textX = Math.round((width - ctx.measureText(text).width) / 2);
                      const textY = height / 2;

                      ctx.fillText(text, textX, textY);
                      ctx.save();
                    }
                  }]}
                />
              </div>
              {/* Custom Legend */}
              <div>
                <h5>Legend</h5>
                <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                  <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '15px', height: '15px', backgroundColor: '#4caf50', marginRight: '20px' }}></div>  Remaning Paid Leave
                  </li>
                  <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '15px', height: '15px', backgroundColor: '#f44336', marginRight: '20px' }}></div> Remaning Unpaid Leave
                  </li>
                  <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '15px', height: '15px', backgroundColor: '#ff9800', marginRight: '20px' }}></div> Remaning Carry Forward
                  </li>
                </ul>
              </div>
            </div>
          </div>     
    </div>
  );
};

export default LeaveBalanceChart;


















